import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

/**
 * Admin-facing bank picker. Fetches `GET /api/banks` once on mount and
 * renders a Shadcn Select. If the currently-selected `value` isn't in the
 * fetched list (e.g. an admin is editing a legacy ATM whose bank was
 * renamed/deleted), the value is still shown as a disabled "legacy" entry
 * at the top so the admin can see what it was before picking a new one.
 *
 * Kept intentionally dumb — no "Other" free-text; admins who need a new
 * bank use the Banks admin tab first.
 */
export default function BankSelect({ value, onChange, disabled, testId = "bank-select", className = "", countryCode = null }) {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = countryCode ? { country_code: countryCode } : {};
    axios.get(`${API}/banks`, { params })
      .then((res) => { if (!cancelled) setBanks(res.data || []); })
      .catch(() => { if (!cancelled) setBanks([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [countryCode]);

  const names = banks.map((b) => b.name);
  const isLegacy = value && !loading && names.length > 0 && !names.includes(value);

  return (
    <Select value={value || undefined} onValueChange={onChange} disabled={disabled || loading}>
      <SelectTrigger
        className={`bg-zinc-800 border-zinc-700 text-white h-8 text-sm ${className}`}
        data-testid={testId}
      >
        <SelectValue placeholder={loading ? "Loading banks…" : "Select a bank"} />
      </SelectTrigger>
      <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
        {isLegacy && (
          <SelectItem value={value} disabled className="text-zinc-500 text-xs" data-testid={`${testId}-legacy`}>
            {value} (legacy — pick a current bank)
          </SelectItem>
        )}
        {banks.map((b) => (
          <SelectItem key={b.id} value={b.name} data-testid={`${testId}-opt-${b.id}`}>
            {b.name}
          </SelectItem>
        ))}
        {!loading && banks.length === 0 && (
          <SelectItem value="__none__" disabled className="text-zinc-500 text-xs">
            No banks — add one in the Banks tab
          </SelectItem>
        )}
      </SelectContent>
    </Select>
  );
}
